// Promo code + Validate + "Book Fitting" CTA — the lower-right of the revealed panel.
//
// The Validate button calls POST /promos/validate (a new endpoint, pending
// in the middleware) with the current code plus the selected fitting and
// location context. On success, the response carries discount details that
// are shown inline below the input. The user can still proceed to book
// without validating — validation is informational, not gating.
//
// State transitions live in the reducer:
//   - SET_PROMO_CODE: editing the code clears any prior validation result
//   - VALIDATE_PROMO_START / SUCCESS / ERROR: standard request lifecycle

import { useState, useEffect, useRef } from 'react';
import styles from '../styles/widget.module.css';
import { validatePromo } from '../api/client';
import { listPriceFor, resolveWireName } from '../data/fittingNames';
import { parsePrice } from '../utils/pricing';
import { isReadOnly } from '../utils/readOnly';
import { TermsPopup } from '../components/TermsPopup';
import { DEFAULT_PROMO_CODE } from '../data/promoTerms';
import { pricingLabelFor } from '../data/fittingTypes';
import { countryConfigOrDefault } from '../config/countries';
import Close from '../assets/icons/Close';
import type { BookingAction, BookingState } from '../state/machine';
import type { PromoDiscount } from '../types';

interface PromoConfirmProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onConfirmFlow: () => void;
}

export function PromoConfirm({ state, dispatch, onConfirmFlow }: PromoConfirmProps) {
  const readOnly = isReadOnly();
  const [termsOpen, setTermsOpen] = useState(false);
  // The promo input collapses behind "Add code +" by default. A Web_Promo
  // deep link (see widget.tsx) seeds a pre-validated code into the reducer
  // before this step renders — auto-reveal the field in that case so the
  // visitor actually SEES their referral promo applied. Initializer only:
  // a code the user typed then hid again doesn't force the field open.
  // Field hidden by default — exposed via "Add code +". The master promo
  // rides on every booking regardless via buildPromoCodeForSchedule, so
  // most customers never need to see this input.
  const [fieldVisible, setFieldVisible] = useState(
    () => Boolean(state.promoCode.trim() && state.promoValidation?.valid),
  );
  // A code applied AFTER this step mounted — the page's data-cc-promoverride
  // validates once the studio is known, which on a store page is after the
  // modal has rendered — must reveal the field too (Jon, 2026-10-13: a valid
  // pinned code stayed behind "Add code +"). Reveal on the transition into a
  // validated non-default code, once per code, so a visitor who hides the
  // field afterwards is not fought.
  const revealedForRef = useRef<string>('');
  useEffect(() => {
    const code = state.promoCode.trim().toUpperCase();
    const validated = state.promoValidatedCode?.trim().toUpperCase() ?? '';
    if (
      code !== '' &&
      code !== DEFAULT_PROMO_CODE &&
      state.promoValidation?.valid === true &&
      validated === code &&
      revealedForRef.current !== code
    ) {
      revealedForRef.current = code;
      setFieldVisible(true);
    }
  }, [state.promoCode, state.promoValidatedCode, state.promoValidation]);
  const canSubmit =
    !readOnly &&
    state.selectedLocation &&
    state.selectedFittingType &&
    state.selectedDate &&
    state.selectedSlot;

  // FITTINGUS is always passed on Schedule regardless of whether the
  // input still shows it. Treat "blank" and "FITTINGUS" as the same
  // no-op state from the Apply button's perspective — there's nothing
  // to apply. The two cases diverge in messaging: blank stays silent,
  // FITTINGUS surfaces the standard "not a valid promo" message so the
  // user knows immediately the literal token isn't acceptable.
  const codeUpper = state.promoCode.trim().toUpperCase();
  const isLiteralFittingus = codeUpper === DEFAULT_PROMO_CODE;
  const isDefaultOrBlank = codeUpper === '' || isLiteralFittingus;
  // A non-FITTINGUS promo is "live applied" when validation succeeded
  // AND the input still matches the validated code. Drives Apply ↔
  // Remove swap below.
  const validatedUpper = state.promoValidatedCode?.trim().toUpperCase() ?? '';
  const isLiveApplied =
    state.promoValidation?.valid === true &&
    validatedUpper.length > 0 &&
    validatedUpper !== DEFAULT_PROMO_CODE &&
    validatedUpper === codeUpper;
  // Apply renders only during the pre-validation phase: user has typed
  // a non-blank, non-FITTINGUS code and hasn't yet hit Apply. Once a
  // validation has happened — success or failure — Apply hides and
  // the Remove X becomes the sole CTA. On failure the user clicks X
  // to reset, then re-reveals the field with Add code + to retry.
  const validationAttempted =
    state.promoValidation !== null || state.promoValidationError !== null;
  const showApply = !isDefaultOrBlank && !validationAttempted;
  // Remove surfaces whenever a validation has happened, regardless of
  // outcome: success keeps the applied promo around; failure gives the
  // user a single way out instead of a stuck disabled Apply.
  const showRemove = validationAttempted;

  // View details surfaces only when the currently-displayed code is a
  // validated, non-FITTINGUS promo. The default FITTINGUS path is
  // implicit (not shown in the UI) and so has no surfaced terms either.
  const showViewDetails = isLiveApplied;

  // Apply requires (a) a non-empty non-FITTINGUS code, (b) all SF
  // /PromoValidate prereqs (location, fitting type, slot), (c) no
  // in-flight call, (d) the code differing from the last one we
  // validated successfully (re-clicking would no-op).
  const prereqsMet =
    Boolean(state.selectedLocation && state.selectedFittingType && state.selectedSlot);
  const canValidate =
    showApply &&
    prereqsMet &&
    !state.promoValidating &&
    state.promoValidatedCode !== state.promoCode.trim();

  const handleValidate = async () => {
    const code = state.promoCode.trim();
    if (!code) return;
    // SF /PromoValidate needs store + fitting + slot + price to evaluate
    // eligibility. The Validate button is only enabled once all four are
    // present, but typescript can't see that from canValidate alone.
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedSlot
    )
      return;

    dispatch({ type: 'VALIDATE_PROMO_START' });
    try {
      const res = await validatePromo({
        promoCode: code,
        locationId: state.selectedLocation.id,
        fittingTypeName: resolveWireName(state.selectedFittingType.name, state.fittingNameMap),
        appointmentStartDateTime: state.selectedSlot.start,
        // The market's price, not the base label's. This used to read
        // `state.selectedFittingType.pricingLabel`, which is the US string, so
        // a UK Full Bag was validated at 400 instead of 250 — and since
        // `originalValue` just echoes what we send, the customer was told
        // their £250 fitting "was US$400".
        // The studio's list price, so the applied line's "(was $X)" is the fee
        // without a purchase; the label's first figure is the with-purchase one.
        price: listPriceFor(
          resolveWireName(state.selectedFittingType.name, state.fittingNameMap),
          state.fittingListPrices,
          parsePrice(pricingLabelFor(state.selectedFittingType, state.country)),
        ),
        email: state.email.trim() || undefined,
      });
      dispatch({
        type: 'VALIDATE_PROMO_SUCCESS',
        payload: {
          code,
          valid: res.valid,
          discount: res.discount,
          message: res.message,
          terms: res.terms,
        },
      });
    } catch (err) {
      dispatch({
        type: 'VALIDATE_PROMO_ERROR',
        payload: err instanceof Error ? err.message : 'Could not validate promo code',
      });
    }
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${styles.stepButtonMobile} ${!canSubmit ? styles.buttonAriaDisabled : ''}`}
        style={{ marginTop: 10 }}
        aria-disabled={!canSubmit}
        data-cc-track="cta"
        data-cc-track-id="book_fitting"
        onClick={() => {
          if (readOnly) return;
          if (!canSubmit) {
            dispatch({ type: 'FLASH_REQUIRED_ERRORS' });
            return;
          }
          onConfirmFlow();
        }}
      >
        {readOnly ? 'Book Fitting (disabled)' : 'Book Fitting'}
      </button>
      <hr className={styles.promoBookDivider} aria-hidden="true" />
      <div className={`${styles.field} ${styles.promoCodeField}`}>
        <label className={styles.label} htmlFor="cc-promo">Referral / PGA Referral / Promo Code (Optional)</label>
        {!fieldVisible ? (
          <button
            type="button"
            onClick={() => setFieldVisible(true)}
            className={styles.stripPillChange}
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
            data-cc-track="cta"
            data-cc-track-id="reveal_promo_field"
          >
            Add code +
          </button>
        ) : (
          <>
            <div className={styles.promoInputRow}>
              <input
                id="cc-promo"
                className={styles.input}
                type="text"
                value={state.promoCode}
                // 10 truncated real codes: PARS5706268 and CUSTOMFITCA are
                // both 11. A truncated code fails validation looking exactly
                // like a wrong one, which is how "the promo code doesn't work"
                // was reported from three markets. 32 is past any code CC has
                // issued while still refusing a pasted paragraph.
                maxLength={32}
                autoFocus
                onChange={(e) =>
                  dispatch({ type: 'SET_PROMO_CODE', payload: e.target.value.toUpperCase() })
                }
                placeholder="Code"
              />
              {showApply && (
                <button
                  type="button"
                  className={styles.validateLink}
                  disabled={!canValidate}
                  data-cc-track="cta"
                  data-cc-track-id="apply_promo"
                  title={!prereqsMet ? 'Pick a location, fitting type, and time before applying a promo code.' : undefined}
                  onClick={handleValidate}
                >
                  {state.promoValidating ? (
                    <>
                      <span className={styles.spinner} />
                      Checking…
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
              )}
              {showRemove && (
                <button
                  type="button"
                  onClick={() => {
                    // Full reset — SET_PROMO_CODE with '' now also clears
                    // promoValidatedCode so the user can re-enter the same
                    // code immediately and have Apply fire fresh.
                    dispatch({ type: 'SET_PROMO_CODE', payload: '' });
                    setFieldVisible(false);
                  }}
                  className={styles.promoRemoveButton}
                  aria-label="Remove code"
                  data-cc-track="cta"
                  data-cc-track-id="remove_promo_field"
                >
                  <Close />
                </button>
              )}
            </div>
            <PromoValidationMessage state={state} forceInvalid={isLiteralFittingus} />
            {showViewDetails && (
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  color: 'var(--cc-iron-gray)',
                  textDecoration: 'underline',
                  fontFamily: 'var(--cc-primary-font)',
                  fontSize: 'var(--cc-text-regular)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginTop: 6,
                  alignSelf: 'flex-start',
                }}
              >
                View details
              </button>
            )}
          </>
        )}
        {/* TermsPopup lives INSIDE .promoCodeField so its absolute
            positioning anchors to the field (position: relative). Was
            rendered outside, which made the right: calc(100% + 12px)
            anchor against the panel grid and effectively positioned
            the popup off-screen. */}
        <TermsPopup
          isOpen={termsOpen}
          onClose={() => setTermsOpen(false)}
          title="Promo Terms & Conditions"
          body={state.promoTerms}
          className={styles.promoViewDetailsPopup}
        />
      </div>
    </>
  );
}

/**
 * Inline status message for the promo field. Three cases:
 *   - network/transport error (e.g., endpoint not yet available)
 *   - valid promo with discount details
 *   - invalid promo with optional server message
 * Nothing renders if no validation has happened.
 */
function PromoValidationMessage({
  state,
  forceInvalid = false,
}: {
  state: BookingState;
  /** When true, render the standard "not a valid promo" copy regardless
   *  of validation state. Used when the user has typed FITTINGUS — the
   *  default token can't be applied as a user-entered code. */
  forceInvalid?: boolean;
}) {
  if (forceInvalid) {
    // This branch IS about the code itself — the house default typed in by
    // hand, which is already applied and cannot be used as a second code.
    return (
      <p className={styles.promoStatusError}>
        This code is already applied to your booking.
      </p>
    );
  }

  if (state.promoValidationError) {
    return (
      <p className={styles.promoStatusError}>
        Couldn’t validate right now — {state.promoValidationError}
      </p>
    );
  }

  if (!state.promoValidation) return null;

  if (!state.promoValidation.valid) {
    // Two problems with the old line. Salesforce sends `message: ""` rather
    // than omitting it, so `?? fallback` never fired and a rejection could
    // render as an empty red line. And the fallback blamed the service, which
    // is usually wrong: the most common rejection is a promotion restricted to
    // new customers being tried by someone who already has a booking. Nothing
    // about the fitting is the problem, and telling somebody to change it
    // sends them off to fix the wrong thing.
    const fromSf = state.promoValidation.message?.trim();
    return (
      <p className={styles.promoStatusError}>
        {fromSf ||
          'We couldn’t apply this code. Some offers are limited to new customers ' +
            'or to particular fittings — check the code and the terms, or call the studio.'}
      </p>
    );
  }

  return (
    <p className={styles.promoStatusOk}>
      ✓ Promo applied{formatDiscount(state.promoValidation.discount, state.country)}
    </p>
  );
}

function formatDiscount(d: PromoDiscount | undefined, country: string | null): string {
  if (!d) return '';
  // The market decides the currency, and Salesforce's field is ignored.
  //
  // It was previously treated as authoritative, on the reasoning that it
  // reflects the store's actual denomination. It does not: CUSTOMFITUK at
  // Crawley returns `{ discountedValue: 100, originalValue: 250,
  // currency: "USD" }` where both numbers are pounds. That printed
  // "service price now US$0 (was US$400)" to a UK customer.
  const cfg = countryConfigOrDefault(country);
  const currency = cfg.currency;
  const fmt = (n: number) =>
    new Intl.NumberFormat(cfg.locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(
      n,
    );

  if (typeof d.discountedValue === 'number') {
    if (typeof d.originalValue === 'number' && d.originalValue > d.discountedValue) {
      // CC's wording: this is the fitting fee, and "service price" is not a
      // term used anywhere else in the flow or on their sites.
      return ` — fitting fee now ${fmt(d.discountedValue)} (was ${fmt(d.originalValue)})`;
    }
    return ` — fitting fee now ${fmt(d.discountedValue)}`;
  }
  if (typeof d.amount === 'number') return ` — ${fmt(d.amount)} off`;
  if (typeof d.percent === 'number') return ` — ${d.percent}% off`;
  return '';
}
